import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import { productService } from '../../services/productService';
import { treatmentPlanService } from '../../services/treatmentPlanService';

const ProductBrowser = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showTreatmentPlanModal, setShowTreatmentPlanModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    complianceStatus: '',
    search: '',
    minRating: ''
  });

  const navigate = useNavigate();

  const categories = ['biological', 'chemical', 'organic', 'biological_controls'];
  const complianceStatuses = ['approved', 'pending', 'restricted', 'prohibited'];

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      loadProducts();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [filters]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await productService?.getProducts(filters);
      setProducts(data || []);
    } catch (err) {
      console.error('Error loading products:', err);
      setError(err?.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      complianceStatus: '',
      search: '',
      minRating: ''
    });
  };

  const toggleProductSelection = (product) => {
    setSelectedProducts(prev => {
      const exists = prev?.find(p => p?.id === product?.id);
      if (exists) {
        return prev?.filter(p => p?.id !== product?.id);
      } else {
        return [...prev, product];
      }
    });
  };

  const handleCreateTreatmentPlan = () => {
    if (selectedProducts?.length === 0) {
      setError('Please select at least one product to create a treatment plan.');
      return;
    }

    // Check authentication before opening modal
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    setShowTreatmentPlanModal(true);
  };

  const handleSignInRedirect = () => {
    setShowAuthModal(false);
    navigate('/login', { 
      state: { 
        from: '/product-browser',
        message: 'Please sign in to create treatment plans',
        selectedProducts: selectedProducts 
      } 
    });
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'biological':
        return 'text-green-700 bg-green-100';
      case 'chemical':
        return 'text-red-700 bg-red-100';
      case 'organic':
        return 'text-blue-700 bg-blue-100';
      case 'biological_controls':
        return 'text-purple-700 bg-purple-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const getComplianceColor = (status) => {
    switch (status) {
      case 'approved':
        return 'text-green-700 bg-green-100';
      case 'pending':
        return 'text-yellow-700 bg-yellow-100';
      case 'restricted':
        return 'text-orange-700 bg-orange-100';
      case 'prohibited':
        return 'text-red-700 bg-red-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const renderRating = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const hasHalfStar = (rating || 0) % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars?.push(<Icon key={i} name="Star" size={16} className="text-yellow-400 fill-current" />);
    }

    if (hasHalfStar) {
      stars?.push(<Icon key="half" name="StarHalf" size={16} className="text-yellow-400 fill-current" />);
    }

    const remainingStars = 5 - Math.ceil(rating || 0);
    for (let i = 0; i < remainingStars; i++) {
      stars?.push(<Icon key={`empty-${i}`} name="Star" size={16} className="text-gray-300" />);
    }

    return <div className="flex items-center space-x-1">{stars}</div>;
  };

  const categoryOptions = categories?.map(cat => ({
    value: cat,
    label: cat?.replace('_', ' ')?.toUpperCase()
  }));

  const complianceOptions = complianceStatuses?.map(status => ({
    value: status,
    label: status?.charAt(0)?.toUpperCase() + status?.slice(1)
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Product Browser</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Browse IPM products and solutions</p>
        </div>

        {/* Filters - Responsive */}
        <div className="mb-6 bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Filter controls with mobile optimization */}
            <div className="sm:col-span-2">
              <Input
                placeholder="Search products..."
                value={filters?.search}
                onChange={(e) => handleFilterChange('search', e?.target?.value)}
                className="w-full"
              />
            </div>

            <Select
              options={[{ value: '', label: 'All Categories' }, ...categoryOptions]}
              value={filters?.category}
              onChange={(value) => handleFilterChange('category', value)}
              placeholder="Category"
            />

            <Select
              options={[{ value: '', label: 'All Statuses' }, ...complianceOptions]}
              value={filters?.complianceStatus}
              onChange={(value) => handleFilterChange('complianceStatus', value)}
              placeholder="Compliance"
            />

            <Select
              options={[
                { value: '', label: 'Any Rating' },
                { value: '4', label: '4+ Stars' },
                { value: '3', label: '3+ Stars' },
                { value: '2', label: '2+ Stars' }
              ]}
              value={filters?.minRating}
              onChange={(value) => handleFilterChange('minRating', value)}
              placeholder="Min Rating"
            />
          </div>
        </div>

        {/* Product Grid - Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {products?.map((product) => {
            const isSelected = selectedProducts?.find(p => p?.id === product?.id);
            
            return (
              <div
                key={product?.id}
                className={`bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-all duration-200 cursor-pointer ${
                  isSelected ? 'ring-2 ring-green-500 border-green-500 shadow-md' : ''
                }`}
                onClick={() => toggleProductSelection(product)}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 flex-1">
                    {product?.name}
                  </h3>
                  <div className="flex items-center space-x-1 ml-2">
                    {isSelected && (
                      <Icon name="Check" size={20} className="text-green-600" />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e?.stopPropagation();
                        setSelectedProduct(product);
                      }}
                      className="opacity-60 hover:opacity-100"
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {/* Category */}
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(product?.category)}`}>
                    {product?.category?.replace('_', ' ')?.toUpperCase()}
                  </div>

                  {/* Compliance Status */}
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2 ${getComplianceColor(product?.complianceStatus)}`}>
                    {product?.complianceStatus?.charAt(0)?.toUpperCase() + product?.complianceStatus?.slice(1)}
                  </div>

                  {/* Rating */}
                  <div className="flex items-center justify-between">
                    {renderRating(product?.rating)}
                    <span className="text-sm text-gray-500">
                      ({product?.reviewCount || 0} reviews)
                    </span>
                  </div>

                  {/* Price */}
                  {product?.price && (
                    <div className="text-lg font-bold text-green-600">
                      {product?.price}
                    </div>
                  )}

                  {/* Active Ingredients */}
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Active:</span> 
                    <span className="line-clamp-2 mt-1">{product?.activeIngredients}</span>
                  </div>

                  {/* EPA Number */}
                  {product?.epaNumer && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">EPA:</span> {product?.epaNumer}
                    </div>
                  )}

                  {/* Targets */}
                  {product?.targets && product?.targets?.length > 0 && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Targets:</span>{' '}
                      <span className="line-clamp-2">
                        {product?.targets?.slice(0, 3)?.map(t => t?.targetName)?.join(', ')}
                        {product?.targets?.length > 3 && ` +${product?.targets?.length - 3} more`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Product Detail Modal Component
const ProductDetailModal = ({ product, onClose, onSelect, isSelected }) => {
  const getCategoryColor = (category) => {
    switch (category) {
      case 'biological':
        return 'text-green-700 bg-green-100';
      case 'chemical':
        return 'text-red-700 bg-red-100';
      case 'organic':
        return 'text-blue-700 bg-blue-100';
      case 'biological_controls':
        return 'text-purple-700 bg-purple-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const getComplianceColor = (status) => {
    switch (status) {
      case 'approved':
        return 'text-green-700 bg-green-100';
      case 'pending':
        return 'text-yellow-700 bg-yellow-100';
      case 'restricted':
        return 'text-orange-700 bg-orange-100';
      case 'prohibited':
        return 'text-red-700 bg-red-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const renderRating = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const hasHalfStar = (rating || 0) % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars?.push(<Icon key={i} name="Star" size={16} className="text-yellow-400 fill-current" />);
    }

    if (hasHalfStar) {
      stars?.push(<Icon key="half" name="StarHalf" size={16} className="text-yellow-400 fill-current" />);
    }

    const remainingStars = 5 - Math.ceil(rating || 0);
    for (let i = 0; i < remainingStars; i++) {
      stars?.push(<Icon key={`empty-${i}`} name="Star" size={16} className="text-gray-300" />);
    }

    return <div className="flex items-center space-x-1">{stars}</div>;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg border border-gray-200 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">{product?.name}</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Category</label>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium mt-1 ${getCategoryColor(product?.category)}`}>
                  {product?.category?.replace('_', ' ')?.toUpperCase()}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600">Compliance Status</label>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium mt-1 ${getComplianceColor(product?.complianceStatus)}`}>
                  {product?.complianceStatus?.charAt(0)?.toUpperCase() + product?.complianceStatus?.slice(1)}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600">Rating</label>
                <div className="flex items-center space-x-2 mt-1">
                  {renderRating(product?.rating)}
                  <span className="text-sm text-gray-500">
                    ({product?.reviewCount || 0} reviews)
                  </span>
                </div>
              </div>

              {product?.price && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Price</label>
                  <div className="text-2xl font-bold text-green-600 mt-1">
                    {product?.price}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {product?.epaNumer && (
                <div>
                  <label className="text-sm font-medium text-gray-600">EPA Number</label>
                  <p className="text-sm text-gray-900 mt-1">{product?.epaNumer}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-600">PHI (Days)</label>
                <p className="text-sm text-gray-900 mt-1">{product?.phi || 0}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600">REI (Hours)</label>
                <p className="text-sm text-gray-900 mt-1">{product?.rei || 0}</p>
              </div>

              {product?.modeOfAction && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Mode of Action</label>
                  <p className="text-sm text-gray-900 mt-1">{product?.modeOfAction}</p>
                </div>
              )}
            </div>
          </div>

          {/* Active Ingredients */}
          <div>
            <label className="text-sm font-medium text-gray-600">Active Ingredients</label>
            <p className="text-sm text-gray-900 mt-1 bg-gray-50 p-3 rounded-md">
              {product?.activeIngredients}
            </p>
          </div>

          {/* Product Information */}
          {product?.productInfo && (
            <div>
              <label className="text-sm font-medium text-gray-600">Product Information</label>
              <p className="text-sm text-gray-900 mt-1 bg-gray-50 p-3 rounded-md">
                {product?.productInfo}
              </p>
            </div>
          )}

          {/* Targets */}
          {product?.targets && product?.targets?.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-600">Target Pests</label>
              <div className="mt-2 space-y-2">
                {product?.targets?.map(target => (
                  <div key={target?.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{target?.targetName}</p>
                      <p className="text-xs text-gray-600">{target?.targetType}</p>
                    </div>
                    {target?.efficacyRating && (
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">{target?.efficacyRating}/5</p>
                        <p className="text-xs text-gray-600">Efficacy</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Application Rate */}
          {product?.applicationRate && (
            <div>
              <label className="text-sm font-medium text-gray-600">Application Rate</label>
              <p className="text-sm text-gray-900 mt-1 bg-gray-50 p-3 rounded-md">
                {product?.applicationRate}
              </p>
            </div>
          )}

          {/* Safety Information */}
          {product?.safetyInfo && (
            <div>
              <label className="text-sm font-medium text-gray-600">Safety Information</label>
              <p className="text-sm text-gray-900 mt-1 bg-gray-50 p-3 rounded-md">
                {product?.safetyInfo}
              </p>
            </div>
          )}

          {/* IPM Notes */}
          {product?.ipmNotes && (
            <div>
              <label className="text-sm font-medium text-gray-600">IPM Notes</label>
              <p className="text-sm text-gray-900 mt-1 bg-gray-50 p-3 rounded-md">
                {product?.ipmNotes}
              </p>
            </div>
          )}

          {/* Links */}
          <div className="flex flex-wrap gap-3">
            {product?.purchaseLink && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(product?.purchaseLink, '_blank')}
              >
                <Icon name="ExternalLink" size={16} className="mr-2" />
                Purchase
              </Button>
            )}
            {product?.alternatePurchaseLink && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(product?.alternatePurchaseLink, '_blank')}
              >
                <Icon name="ExternalLink" size={16} className="mr-2" />
                Alt Purchase
              </Button>
            )}
            {product?.productLabelLink && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(product?.productLabelLink, '_blank')}
              >
                <Icon name="FileText" size={16} className="mr-2" />
                Label
              </Button>
            )}
            {product?.sdsLink && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(product?.sdsLink, '_blank')}
              >
                <Icon name="AlertTriangle" size={16} className="mr-2" />
                SDS
              </Button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                onSelect(product);
                onClose();
              }}
              className="flex items-center space-x-2"
            >
              <Icon name={isSelected ? "Check" : "Plus"} size={16} />
              <span>{isSelected ? "Selected" : "Select Product"}</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Authentication Required Modal Component
const AuthRequiredModal = ({ onClose, onSignIn, selectedCount }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg border border-gray-200 max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Sign In Required</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <Icon name="Lock" size={32} className="text-green-600" />
            </div>
          </div>

          <div className="text-center space-y-2">
            <p className="text-gray-900 font-medium">
              You need to sign in to create treatment plans
            </p>
            <p className="text-gray-600 text-sm">
              You have selected {selectedCount} product{selectedCount !== 1 ? 's' : ''}. 
              Sign in to save your selection and create a treatment plan.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Why sign in?</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Save your treatment plans</li>
                  <li>Track treatment history</li>
                  <li>Access advanced features</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between p-6 border-t border-gray-200 space-x-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Continue Browsing
          </Button>
          <Button onClick={onSignIn} className="flex-1 bg-green-600 hover:bg-green-700">
            <Icon name="LogIn" size={16} className="mr-2" />
            Sign In
          </Button>
        </div>
      </div>
    </div>
  );
};

// Treatment Plan Creation Modal Component
const TreatmentPlanModal = ({ selectedProducts, onClose, onSave }) => {
  const { user } = useAuth();
  const [planName, setPlanName] = useState('');
  const [aiReasoning, setAiReasoning] = useState('');
  const [safetyNotes, setSafetyNotes] = useState('');
  const [estimatedCost, setEstimatedCost] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!planName?.trim()) {
      setError('Plan name is required');
      return;
    }

    if (!user) {
      setError('You must be signed in to create a treatment plan');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const planData = {
        planName: planName?.trim(),
        aiReasoning: aiReasoning?.trim(),
        safetyNotes: safetyNotes?.trim(),
        estimatedCost: estimatedCost ? parseFloat(estimatedCost) : 0
      };

      await treatmentPlanService?.createTreatmentPlan(planData, selectedProducts);
      onSave();
    } catch (err) {
      console.error('Error creating treatment plan:', err);
      setError(err?.message || 'Failed to create treatment plan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg border border-gray-200 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Create Treatment Plan</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-red-600" />
                <span className="text-red-800 text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Selected Products Summary */}
          <div>
            <label className="text-sm font-medium text-gray-600">Selected Products ({selectedProducts?.length})</label>
            <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
              {selectedProducts?.map(product => (
                <div key={product?.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{product?.name}</p>
                    <p className="text-xs text-gray-600">{product?.category}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-900">{product?.price}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Plan Name */}
          <div>
            <label className="text-sm font-medium text-gray-600">Plan Name *</label>
            <Input
              value={planName}
              onChange={(e) => setPlanName(e?.target?.value)}
              placeholder="Enter treatment plan name..."
              className="mt-1"
            />
          </div>

          {/* AI Reasoning */}
          <div>
            <label className="text-sm font-medium text-gray-600">Description / Reasoning</label>
            <textarea
              value={aiReasoning}
              onChange={(e) => setAiReasoning(e?.target?.value)}
              placeholder="Describe the treatment strategy and reasoning..."
              rows={4}
              className="mt-1 w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>

          {/* Safety Notes */}
          <div>
            <label className="text-sm font-medium text-gray-600">Safety Notes</label>
            <textarea
              value={safetyNotes}
              onChange={(e) => setSafetyNotes(e?.target?.value)}
              placeholder="Important safety considerations..."
              rows={3}
              className="mt-1 w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>

          {/* Estimated Cost */}
          <div>
            <label className="text-sm font-medium text-gray-600">Estimated Cost ($)</label>
            <Input
              type="number"
              value={estimatedCost}
              onChange={(e) => setEstimatedCost(e?.target?.value)}
              placeholder="0.00"
              step="0.01"
              min="0"
              className="mt-1"
            />
          </div>
        </div>

        <div className="flex justify-between p-6 border-t border-gray-200">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? (
              <>
                <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Treatment Plan'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductBrowser;